import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from '../ui/card';
import AnswerOption from '../shared/AnswerOption';
import CountdownTimer from '../shared/CountdownTimer';
import type { Question } from '../../types';

interface QuizParticipantProps {
  currentQuestion: Question | null;
  isWaiting: boolean;
  showAnswer: boolean;
  selectedAnswer?: number;
  score: number;
  onSelectAnswer: (optionIndex: number) => void;
  questionStartTime?: number;
}

const QuizParticipant: React.FC<QuizParticipantProps> = ({
  currentQuestion,
  isWaiting,
  showAnswer,
  selectedAnswer,
  score,
  onSelectAnswer,
  questionStartTime,
}) => {
  const [localSelectedAnswer, setLocalSelectedAnswer] = useState<number | undefined>(selectedAnswer);
  const [answered, setAnswered] = useState(false);
  const [scoreAnimation, setScoreAnimation] = useState(false);
  
  useEffect(() => {
    setLocalSelectedAnswer(undefined);
    setAnswered(false);
  }, [currentQuestion, isWaiting]);
  
  // Animate score when it changes
  useEffect(() => {
    setScoreAnimation(true);
    const timeout = setTimeout(() => setScoreAnimation(false), 1000);
    return () => clearTimeout(timeout);
  }, [score]);
  
  const handleSelectAnswer = (optionIndex: number) => {
    if (answered || isWaiting) return;
    
    setLocalSelectedAnswer(optionIndex);
    setAnswered(true);
    onSelectAnswer(optionIndex);
  };
  

  
  if (isWaiting && !showAnswer) {
    return (
      <Card className="text-center">
        <CardHeader>
          <CardTitle>Waiting for the next question...</CardTitle>
        </CardHeader>
        <CardContent className="py-12">
          <div className="animate-pulse text-6xl mb-4">⏳</div>
          <CardDescription>
            The teacher will advance to the next question soon
          </CardDescription>
        </CardContent>
        <CardFooter className="justify-center">
          <div className={`text-xl font-bold ${scoreAnimation ? 'score-increase' : ''}`}>
            Your score: {score}
          </div>
        </CardFooter>
      </Card>
    );
  }
  
  if (isWaiting && showAnswer && currentQuestion) {
    const userAnsweredCorrectly = localSelectedAnswer !== undefined && currentQuestion.options[localSelectedAnswer]?.isCorrect;
    
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-card rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.08)] p-8 mb-6">
          <h2 className="font-heading font-bold text-3xl text-foreground mb-8">
            {currentQuestion.text}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {currentQuestion.options.map((option, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border-2 transition-all ${
                  option.isCorrect
                    ? 'bg-green-50 border-green-500'
                    : localSelectedAnswer === index
                    ? 'bg-red-50 border-red-500'
                    : 'bg-white border-border'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${
                      option.isCorrect
                        ? 'bg-green-500 text-white'
                        : localSelectedAnswer === index
                        ? 'bg-red-500 text-white'
                        : 'bg-primary text-primary-foreground'
                    }`}>
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span className="text-lg">{option.text}</span>
                  </div>
                  {option.isCorrect && (
                    <span className="text-2xl text-green-600">✓</span>
                  )}
                  {!option.isCorrect && localSelectedAnswer === index && (
                    <span className="text-2xl text-red-600">✗</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center py-4 mb-4">
            {localSelectedAnswer !== undefined ? (
              <div className={`text-2xl font-bold font-heading ${
                userAnsweredCorrectly ? 'text-green-600' : 'text-red-600'
              }`}>
                {userAnsweredCorrectly ? '✓ Correct!' : '✗ Incorrect'}
              </div>
            ) : (
              <div className="text-xl font-semibold text-muted-foreground">
                You did not answer this question
              </div>
            )}
          </div>
          
          <div className="text-center py-4 border-t">
            <p className="text-muted-foreground">Waiting for next question...</p>
          </div>
        </div>
        
        <div className="bg-foreground text-background rounded-full px-8 py-4 text-center shadow-lg inline-block">
          <span className={`text-xl font-bold ${scoreAnimation ? 'score-increase' : ''}`}>
            Score: {score}
          </span>
        </div>
      </div>
    );
  }
  
  if (!currentQuestion) {
    return (
      <Card className="text-center">
        <CardHeader>
          <CardTitle>No active question</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Waiting for the quiz to start...</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.08)] p-8 mb-6">
        <div className="flex justify-between items-start mb-8">
          <h2 className="font-heading font-bold text-3xl text-foreground flex-1">
            {currentQuestion.text}
          </h2>
          {!answered && !showAnswer && (
            <div className="ml-6 flex-shrink-0">
              <div className="bg-primary text-primary-foreground rounded-full w-20 h-20 flex items-center justify-center shadow-lg">
                <CountdownTimer 
                  timeLimit={currentQuestion.timeLimit} 
                  onTimeUp={() => setAnswered(true)}
                  startTime={questionStartTime}
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {currentQuestion.options.map((option, index) => (
            <AnswerOption
              key={index}
              option={option}
              index={index}
              selected={localSelectedAnswer === index}
              disabled={answered || showAnswer}
              onClick={() => handleSelectAnswer(index)}
            />
          ))}
        </div>
        
        {showAnswer && (
          <div className="text-center py-4">
            {localSelectedAnswer !== undefined && (
              <div className={`text-2xl font-bold font-heading ${
                currentQuestion.options[localSelectedAnswer].isCorrect
                  ? 'text-green-600'
                  : 'text-destructive'
              }`}>
                {currentQuestion.options[localSelectedAnswer].isCorrect
                  ? '✓ Correct!'
                  : '✗ Incorrect'}
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="bg-foreground text-background rounded-full px-8 py-4 text-center shadow-lg inline-block">
        <div className="flex items-center gap-4">
          {answered && !showAnswer && (
            <span className="text-primary-foreground font-semibold">
              Answer submitted!
            </span>
          )}
          <span className={`text-xl font-bold ${scoreAnimation ? 'score-increase' : ''}`}>
            Score: {score}
          </span>
        </div>
      </div>
    </div>
  );
};

export default QuizParticipant;